// React
import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";
// MUI
import { Box, Fade, Grid, Stack, Typography } from "@mui/material";
// Helpers
import { pushUniqueValue, removeListValue } from "@/Common/helpers/Arrays";
// Interfaces
import { IAccessListsProps, TAccessType } from "../interfaces";
// Hooks
import { useAccessListUsers } from "../hooks/useAccessListData";
// Components
import MultiSelectFilterButtons from "../../FloatingTabButtonSelector/MultiSelectFilterButtons";
import { LoadingSpinner } from "../../LoadingSpinner";
import AccessListsContent from "../AccessListsContent";
import SearchInput from "../../Basics/SearchInput";
import { groupsBySearchCriteria } from "@/Common/helpers/Search";
import { ISearchItem } from "@/Common/helpers/Search/interfaces";
import { AccessListLoading } from "../AccessListLoading";
import { or } from "@/Common/helpers/sonar";
const getSearchCriteria = (type: TAccessType) => {
	if (type === "users") {
		return {
			root: "position",
			nodes: ["fullName", "position", "locationName"],
		};
	}
	return {};
};

const USERS_TYPE_OPTION_TAB = ["Local Users", "Other Users"];

export const AccessListUsers = (props: IAccessListsProps) => {
	const {
		access = [],
		locationId = "",
		titleField = "",
		searchable = false,
		hasTabs = true,
		accessType = "users",
		descriptionField = "",
		onChangeSelection,
		preloadedData = [],
	} = props;

	const [tabSelected, setTabSelected] = useState(USERS_TYPE_OPTION_TAB[0]);
	const [assigned, setAssigned] = useState<string[]>([]);
	const [checked, setChecked] = useState<string[]>([]);
	const [search, setSearch] = useState("");
	const [searchResultsCount, setSearchResultsCount] = useState(0);

	const { root, nodes = [] } = getSearchCriteria(accessType);
	const isLocalUserShown = tabSelected === USERS_TYPE_OPTION_TAB[0];

	const {
		data: localUsers,
		isLoading: isLoadingLocalUsers,
		isFetching: isFetchingLocalUsers,
	} = useAccessListUsers(locationId, "local", preloadedData);

	const {
		data: otherUsers,
		isLoading: isLoadingOtherUsers,
		isFetching: isFetchingOtherUsers,
	} = useAccessListUsers(locationId, "external", preloadedData);

	const intersectionRef = useRef(null);
	const loaderBasicFeedback = `Loading ${accessType}`;
	const loaderExtraFeedbackLocal = " for current location";
	const loaderExtraFeedbackExternal = " from other locations";

	const loaderFullFeedbackLocal = `${loaderBasicFeedback}${loaderExtraFeedbackLocal}`;
	const loaderFullFeedbackExternal = `${loaderBasicFeedback}${loaderExtraFeedbackExternal}`;

	const handleChangeAreaCheckbox = (event: React.ChangeEvent<HTMLInputElement>, area: string) => {
		let selections = [...checked];
		const areaItems: ISearchItem[] =
			tabSelected === USERS_TYPE_OPTION_TAB[0]
				? (localUsers?.filter((item) => (root ? item?.[root as keyof typeof item] === area : false)) as unknown as ISearchItem[]) ??
				  []
				: (otherUsers?.filter((item) => (root ? item?.[root as keyof typeof item] === area : false)) as unknown as ISearchItem[]) ??
				  [];

		const [{ list: availableItems = [] } = {}] = groupsBySearchCriteria(areaItems, { root: root ?? null, nodes, search });
		let keys = availableItems.filter((item) => !item.filteredOut).map((item) => (item as ISearchItem & { _id: string })._id);

		if (event.target.checked) {
			keys = keys.filter((key) => !assigned.includes(key));
			if (keys.length) {
				keys.forEach((key) => {
					selections = pushUniqueValue(selections, key);
				});
			}
		} else if (keys.length) {
			keys.forEach((key) => {
				selections = removeListValue(selections, key);
			});
		}

		const selectedItems = choiceDataByTabSelected(selections);
		onChangeSelection(selectedItems ?? []);
		setChecked(selectedItems?.map((item) => item._id) ?? []);
	};

	const handleChangeCheckbox = (value: string) => {
		let selections = [...checked];
		if (selections.includes(value)) {
			selections = selections.filter((element) => element !== value);
		} else {
			selections.push(value);
		}
		const selectedItems = choiceDataByTabSelected(selections);
		onChangeSelection?.(selectedItems ?? []);
		setChecked(selections);
	};

	const choiceDataByTabSelected = (selections: string[]) => {
		return tabSelected === USERS_TYPE_OPTION_TAB[0]
			? localUsers?.filter((element) => selections.includes(element._id))
			: otherUsers?.filter((element) => selections.includes(element._id));
	};

	useEffect(() => {
		const currentAssigned = access?.map((action) => action._id) as string[];
		setAssigned(currentAssigned);
		return () => {
			setAssigned([]);
			setChecked([]);
			setSearch("");
		};
	}, [access]);

	const selectedUserCounts: Record<string, number> = useMemo(() => {
		const assignedSet = new Set(assigned);
		const checkedSet = new Set(checked);

		const localIds = new Set(localUsers.map((user) => user._id));
		const otherIds = new Set(otherUsers.map((user) => user._id));

		let localCount = 0;
		let otherCount = 0;

		for (const id of assignedSet) {
			if (localIds.has(id)) {
				localCount++;
			} else if (otherIds.has(id)) {
				otherCount++;
			}
		}
		for (const id of checkedSet) {
			if (localIds.has(id)) {
				localCount++;
			} else if (otherIds.has(id)) {
				otherCount++;
			}
		}
		return {
			"Local Users": localCount,
			"Other Users": otherCount,
		};
	}, [localUsers, otherUsers, assigned, checked]);

	if (
		or(
			isLoadingLocalUsers,
			isLoadingOtherUsers,
			isLocalUserShown && localUsers?.length === 0,
			!isLocalUserShown && otherUsers?.length === 0
		)
	) {
		return (
			<>
				<AccessListLoading accessType={accessType} searchable={searchable} hasTabs={hasTabs} />
				<Fade in={isFetchingLocalUsers} timeout={400}>
					<Box className="absolute bottom-0 ml-6 mb-6 font-sans text-[14.5px] text-default">
						<LoadingSpinner label={`${loaderBasicFeedback}...`} className="items-center" />
					</Box>
				</Fade>
			</>
		);
	}

	return (
		<>
			{searchable && (
				<Stack justifyContent="center" className={clsx("pt-2", { "pb-8": accessType !== "users" })}>
					<Grid container className="bg-white" justifyContent="center">
						<Grid item xs={6}>
							<SearchInput value={search} onChange={(value) => setSearch(value)} clearSearch={() => setSearch("")} />
							{accessType === "users" && (
								<Typography
									className={clsx("text-center w-full !font-sans !text-[14.5px] pt-[10px]", {
										"text-neutral-gray": search,
										"text-transparent": !search,
									})}
								>
									Showing {searchResultsCount} results
								</Typography>
							)}
						</Grid>
					</Grid>
				</Stack>
			)}

			<Stack justifyContent="center" className={clsx("pt-2", "pb-8")}>
				<Grid container className="bg-white" justifyContent="center">
					<MultiSelectFilterButtons
						filterOptions={USERS_TYPE_OPTION_TAB}
						selectedTab={tabSelected}
						onChangeTab={(selectedTab) => setTabSelected(selectedTab)}
						decorateLabel={(label, selected) => {
							const count = selectedUserCounts[label] ?? 0;
							const pillClass = clsx(
								"!font-sans !text-[12px]",
								"w-5 h-5 rounded-full border border-1px p-1 flex items-center justify-center",
								selected ? "!text-white !bg-primary border-primary" : "!text-primary"
							);
							return (
								<Typography component="span" className="flex flex-row items-center gap-2">
									{label}
									<Typography component="span" className={pillClass}>
										{count}
									</Typography>
								</Typography>
							);
						}}
					/>
				</Grid>
			</Stack>
			<Stack ref={intersectionRef} className="py-0 mx-2 pl-4 pr-3">
				<AccessListsContent
					data={isLocalUserShown ? localUsers : otherUsers}
					intersectionRef={intersectionRef}
					checked={checked}
					selections={assigned}
					accessType={accessType}
					titleField={titleField}
					descriptionField={descriptionField}
					search={search}
					handleChangeCheckbox={handleChangeCheckbox}
					handleChangeAreaCheckbox={handleChangeAreaCheckbox}
					handleChangeSearchResultsCount={setSearchResultsCount}
				/>
			</Stack>
			<Fade in={isLocalUserShown ? isFetchingLocalUsers : isFetchingOtherUsers} timeout={400}>
				<Box className="absolute bottom-0 ml-6 mb-6 font-sans text-[14.5px] text-default">
					<LoadingSpinner
						label={`${isLocalUserShown ? loaderFullFeedbackLocal : loaderFullFeedbackExternal}...`}
						className="items-center"
					/>
				</Box>
			</Fade>
		</>
	);
};
